import React, { useEffect, useMemo, useState } from "react";
import {
  ShieldCheck,
  History,
  Percent,
  Ban,
  ArrowRight,
  Lock,
  Power,
  CheckCircle,
  X as XIcon,
} from "lucide-react";
import { toast } from "react-toastify";
import { useLocation, useNavigate } from "react-router-dom";

const rolesSeed = [
  { id: 1, name: "Serveur", canDiscount: false, cancelStatuses: ["pending"] },
  { id: 2, name: "Caissier", canDiscount: true, cancelStatuses: ["pending"] },
  { id: 3, name: "Manager", canDiscount: true, cancelStatuses: ["pending", "preparing", "ready", "served"] },
  { id: 4, name: "Admin", canDiscount: true, cancelStatuses: ["pending", "preparing", "ready", "served", "done"] },
];

const ORDER_STATUSES = [
  { key: "pending", label: "En attente" },
  { key: "preparing", label: "En préparation" },
  { key: "ready", label: "Prêt à être servi" },
  { key: "served", label: "Servi" },
  { key: "done", label: "Terminé" },
];

const cardBase =
  "rounded-2xl border transition shadow-lg overflow-hidden bg-white dark:bg-[#0b1328]";

const Permissions = () => {
  const [roles, setRoles] = useState(rolesSeed);
  const [activeCard, setActiveCard] = useState(null);
  const [showPoliciesModal, setShowPoliciesModal] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const adminBasePath = location.pathname.startsWith("/superadmin")
    ? "/superadmin"
    : "/admin";

  const isDarkMode = useMemo(
    () =>
      typeof document !== "undefined"
        ? document.documentElement.classList.contains("dark")
        : true,
    [],
  );

  const handleToggleDiscount = (roleId) => {
    setRoles((prev) =>
      prev.map((role) =>
        role.id === roleId
          ? { ...role, canDiscount: !role.canDiscount }
          : role,
      ),
    );
  };

  const handleToggleStatus = (roleId, statusKey) => {
    setRoles((prev) =>
      prev.map((role) => {
        if (role.id !== roleId) return role;
        const exists = role.cancelStatuses.includes(statusKey);
        return {
          ...role,
          cancelStatuses: exists
            ? role.cancelStatuses.filter((s) => s !== statusKey)
            : [...role.cancelStatuses, statusKey],
        };
      }),
    );
  };

  const accentText = isDarkMode ? "text-white/80" : "text-slate-700";
  const mutedText = isDarkMode ? "text-white/60" : "text-slate-500";

  useEffect(() => {
    let active = true;
    const loadFromStorage = () => {
      try {
        const stored = localStorage.getItem("moo_permissions_policy");
        if (stored) {
          const parsed = JSON.parse(stored);
          if (parsed?.roles) {
            setRoles(parsed.roles);
          }
        }
      } catch (err) {
        console.warn("Impossible de charger les permissions stockées:", err);
      }
    };

    const loadPolicy = async () => {
      try {
        const response = await fetch("/api/permissions/policy");
        if (response.ok) {
          const data = await response.json();
          if (active && data?.policy?.roles) {
            setRoles(data.policy.roles);
            try {
              localStorage.setItem(
                "moo_permissions_policy",
                JSON.stringify(data.policy),
              );
            } catch (_e) {}
            return;
          }
        }
      } catch (_err) {
        // ignore
      }
      if (active) loadFromStorage();
    };

    loadPolicy();
    return () => {
      active = false;
    };
  }, []);

  const showSuccessToast = (message) => {
    toast(
      ({ closeToast }) => (
        <div className="bg-slate-950/95 text-white border border-white/10 rounded-2xl shadow-2xl shadow-black/40 backdrop-blur-lg px-4 py-3 relative">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-2xl flex items-center justify-center bg-emerald-500/15 text-emerald-300">
              <CheckCircle className="w-5 h-5" />
            </div>
            <div className="text-sm text-white">
              <p className="font-semibold leading-tight text-white">Succès</p>
              <p className="text-white/70 leading-tight">{message}</p>
            </div>
          </div>
          <button
            type="button"
            aria-label="Fermer la notification"
            onClick={closeToast}
            className="absolute top-3 right-3 text-white/50 hover:text-white transition-colors"
          >
            <XIcon className="w-4 h-4" />
          </button>
        </div>
      ),
        {
          autoClose: 3200,
          closeButton: false,
          hideProgressBar: false,
          theme: "dark",
          progressClassName:
            "Toastify__progress-bar-theme--dark Toastify__progress-bar--default bg-white/30",
          className: "bg-transparent shadow-none border-none p-0",
          bodyClassName: "!p-0",
        },
    );
  };

  const persistPolicies = async () => {
    const payload = {
      roles,
      updatedAt: new Date().toISOString(),
    };
    try {
      localStorage.setItem("moo_permissions_policy", JSON.stringify(payload));
    } catch (err) {
      console.warn("Impossible d'enregistrer les permissions localement:", err);
    }
    try {
      const response = await fetch("/api/permissions/policy", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ policy: payload }),
      });
      if (!response.ok) {
        throw new Error(`http_${response.status}`);
      }
    } catch (err) {
      console.warn("Impossible d'enregistrer les permissions côté serveur:", err);
    }
  };

  const PlaceholderCard = ({ icon: Icon, title, description }) => (
    <div
      className={`${cardBase} border-white/10 dark:border-white/10 border-gray-100 opacity-70 relative overflow-hidden`}
    >
      <div className="absolute inset-0 bg-gradient-to-br from-white/60 via-white/30 to-transparent dark:from-white/10 dark:via-white/5 pointer-events-none" />
      <div className="flex items-center gap-3 px-5 py-4 bg-gradient-to-r from-slate-200/50 to-slate-100/30 dark:from-white/5 dark:to-white/0 relative">
        <div className="p-3 rounded-2xl bg-white/80 dark:bg-white/10 shadow">
          <Icon className="w-5 h-5 text-amber-500" />
        </div>
        <div>
          <div className="inline-flex items-center gap-2 rounded-full bg-amber-500/15 px-3 py-1 text-xs font-semibold text-amber-500">
            <Lock size={12} /> Bientôt
          </div>
          <h3 className="text-lg font-semibold text-gray-800 dark:text-white mt-2">
            {title}
          </h3>
        </div>
      </div>
      <div className="px-5 py-4 text-sm text-gray-600 dark:text-white/60">
        {description}
      </div>
    </div>
  );

  const cards = [
    {
      id: "cancellations",
      title: "Historique des annulations",
      description:
        "Consultez toutes les annulations effectuées dans le système.",
      icon: History,
      active: true,
      onClick: () => navigate(`${adminBasePath}/permissions/cancellations`),
    },
    {
      id: "discounts",
      title: "Historique des remises",
      description:
        "Consultez toutes les remises appliquées en caisse et exportez les traces.",
      icon: Percent,
      active: true,
      onClick: () => navigate(`${adminBasePath}/permissions/discounts`),
    },
    {
      id: "policies",
      title: "Politiques de remises et d’annulation",
      description:
        "Définissez les droits de remise et d’annulation par rôle et par statut.",
      icon: ShieldCheck,
      active: true,
    },
  ];

  return (
    <div className="max-w-6xl mx-auto px-4 py-8 space-y-6">
      <div className="grid gap-4 md:grid-cols-3">
        {cards.map((card) =>
          card.active ? (
            <button
              key={card.id}
              type="button"
              onClick={() => {
                setActiveCard(card.id);
                if (card.onClick) {
                  card.onClick();
                } else {
                  setShowPoliciesModal(true);
                }
              }}
              className={`${cardBase} border-white/10 dark:border-white/10 border-gray-200 text-left group ${
                activeCard === card.id ? "ring-2 ring-amber-400/60" : ""
              }`}
            >
              <div className="flex items-center gap-3 px-5 py-4 bg-gradient-to-r from-amber-500/20 via-orange-400/15 to-pink-500/10">
                <div className="p-3 rounded-2xl bg-white/70 dark:bg-white/10 shadow">
                  <card.icon className="w-5 h-5 text-amber-400" />
                </div>
                <div>
                  <p className="text-xs uppercase tracking-[0.25em] text-amber-300">
                    Actif
                  </p>
                  <h3 className="text-lg font-semibold text-gray-800 dark:text-white">
                    {card.title}
                  </h3>
                </div>
                <ArrowRight className="ml-auto w-4 h-4 text-amber-300 opacity-0 group-hover:opacity-100 transition" />
              </div>
              <div className="px-5 py-4 text-sm text-gray-600 dark:text-white/70">
                {card.description}
              </div>
            </button>
          ) : (
            <PlaceholderCard
              key={card.id}
              icon={card.icon}
              title={card.title}
              description={card.description}
            />
          ),
        )}
      </div>

      {activeCard === "policies" && showPoliciesModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
          <div className="w-full max-w-5xl mx-4 rounded-3xl bg-white dark:bg-[#0b1328] border border-white/10 shadow-2xl overflow-hidden animate-fadeIn">
            <div className="flex items-center justify-between px-6 py-4 border-b border-white/10 bg-gradient-to-r from-amber-500/20 via-orange-500/15 to-pink-500/10">
              <div>
                <p className="text-xs uppercase tracking-[0.25em] text-amber-300">
                  Permissions actives
                </p>
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                  Politiques de remises et d’annulation
                </h3>
              </div>
              <button
                type="button"
                onClick={() => setShowPoliciesModal(false)}
                className="rounded-full bg-white/10 text-white hover:bg-white/20 px-3 py-2 text-sm font-semibold"
              >
                Fermer
              </button>
            </div>
            <div className="px-6 py-5 space-y-4 max-h-[75vh] overflow-y-auto">
              <p className={`text-sm ${mutedText}`}>
                Définissez quels rôles peuvent appliquer des remises et sur quels statuts une annulation reste autorisée.
              </p>

              <div className="space-y-3">
                <h4 className="text-sm font-semibold text-gray-800 dark:text-white flex items-center gap-2">
                  <Percent className="w-4 h-4 text-amber-400" />
                  Permissions de remises
                </h4>
                <div className="rounded-2xl border border-white/10 dark:border-white/10 border-gray-200 overflow-hidden">
                  <div className="grid grid-cols-2 bg-gray-50/70 dark:bg-white/5 px-4 py-2 text-xs font-semibold text-gray-700 dark:text-white/70">
                    <span>Rôle</span>
                    <span className="text-right">Peut appliquer une remise</span>
                  </div>
                  <div className="divide-y divide-white/10 dark:divide-white/10 divide-gray-200">
                    {roles.map((role) => (
                      <div
                        key={role.id}
                        className="grid grid-cols-2 items-center px-4 py-3 text-sm"
                      >
                        <span className={accentText}>{role.name}</span>
                        <div className="text-right">
                          <button
                            type="button"
                            onClick={() => handleToggleDiscount(role.id)}
                            className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full border transition ${
                              role.canDiscount
                                ? "border-amber-400/60 bg-amber-400/15 text-amber-300"
                                : "border-white/15 bg-white/5 text-white/70"
                            }`}
                          >
                            <Power className="w-4 h-4" />
                            <span className="text-xs font-semibold">
                              {role.canDiscount ? "Autorisé" : "Désactivé"}
                            </span>
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="space-y-3">
                <h4 className="text-sm font-semibold text-gray-800 dark:text-white flex items-center gap-2">
                  <Ban className="w-4 h-4 text-red-400" />
                  Permissions d’annulation par statut
                </h4>
                <div className="rounded-2xl border border-white/10 dark:border-white/10 border-gray-200 overflow-hidden">
                  <div className="grid grid-cols-[1.4fr_repeat(5,1fr)] bg-gray-50/70 dark:bg-white/5 px-4 py-2 text-xs font-semibold text-gray-700 dark:text-white/70">
                    <span>Rôle</span>
                    {ORDER_STATUSES.map((s) => (
                      <span key={s.key} className="text-center">
                        {s.label}
                      </span>
                    ))}
                  </div>
                  <div className="divide-y divide-white/10 dark:divide-white/10 divide-gray-200">
                    {roles.map((role) => (
                      <div
                        key={role.id}
                        className="grid grid-cols-[1.4fr_repeat(5,1fr)] items-center px-4 py-3 text-sm"
                      >
                        <span className={accentText}>{role.name}</span>
                        {ORDER_STATUSES.map((status) => {
                          const checked = role.cancelStatuses.includes(status.key);
                          return (
                            <div key={status.key} className="flex justify-center">
                              <button
                                type="button"
                                onClick={() => handleToggleStatus(role.id, status.key)}
                                className={`p-2 rounded-full border transition ${
                                  checked
                                    ? "border-emerald-400/60 bg-emerald-400/15 text-emerald-200"
                                    : "border-white/15 bg-white/5 text-white/70"
                                }`}
                                title={checked ? "Autorisé" : "Désactivé"}
                              >
                                <Power className="w-4 h-4" />
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    ))}
                  </div>
                </div>
                <p className={`text-xs ${mutedText}`}>
                  Ces règles devront être appliquées côté caisse : le bouton “Annuler” sera autorisé uniquement si le rôle de l’utilisateur et le statut de la commande correspondent aux permissions définies.
                </p>
              </div>
            </div>
            <div className="flex justify-end gap-3 px-6 py-4 border-t border-white/10 bg-white/5 dark:bg-black/20">
              <button
                type="button"
                onClick={() => setShowPoliciesModal(false)}
                className="rounded-2xl px-4 py-2 text-sm font-semibold border border-white/15 text-gray-700 dark:text-white hover:border-amber-300/60"
              >
                Annuler
              </button>
              <button
                type="button"
                onClick={async () => {
                  setShowPoliciesModal(false);
                  await persistPolicies();
                  showSuccessToast("Permissions mises à jour");
                }}
                className="rounded-2xl px-4 py-2 text-sm font-semibold bg-gradient-to-r from-amber-400 to-orange-400 text-slate-900 shadow"
              >
                Valider
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Permissions;
